import { PlatformError } from '@djgg/core';
import { validateJsonObject } from '@djgg/gameplay-kit';

/** Runtime features the trusted version row may advertise before a server
 * fetches and verifies the complete immutable sidecar. Feature names are
 * compatibility declarations, never client-selected switches. */
export const RUNTIME_CONTRACT_FEATURES = [
  'demo-plan',
  'effects-binding',
  'match-hud',
  'per-viewer-projection',
  'scene-composition',
  'version-media',
] as const;

export type RuntimeContractFeature = (typeof RUNTIME_CONTRACT_FEATURES)[number];

/**
 * Trusted, bounded metadata stored on `creation_versions.runtime_contract`.
 *
 * The full CreationRuntimeContractV1 is an immutable sidecar in object storage.
 * New publishers use `runtime-contract.<sha256>.json`; the original fixed-name
 * `runtime-contract.json` remains readable for already-published versions. This
 * ref is deliberately only enough to locate and verify that sidecar before any
 * generated sim bytes are read or imported.
 */
export interface RuntimeContractRefV1 {
  path: string;
  sha256: string;
  schemaVersion: 1;
  protocolVersion: 1 | 2;
  capabilityDigest: string;
  features: RuntimeContractFeature[];
}

const SHA256 = /^[0-9a-f]{64}$/;
const STORAGE_PATH = /^gs:\/\/[^/]+\/.+/;

function fail(path: string, detail: string): never {
  throw new PlatformError('validation', `invalid RuntimeContractRefV1 at ${path}: ${detail}`);
}

function record(value: unknown, path: string): Record<string, unknown> {
  try { return validateJsonObject(value, path); }
  catch (error) { fail(path, error instanceof Error ? error.message : 'must be a strict JSON object'); }
}

function ownOnly(value: Record<string, unknown>, path: string, keys: readonly string[]): void {
  for (const key of Object.keys(value)) {
    if (!keys.includes(key)) fail(path, `has unknown key ${JSON.stringify(key)}`);
  }
}

function digest(value: unknown, path: string): string {
  if (typeof value !== 'string' || !SHA256.test(value)) {
    fail(path, 'must be a 64-character lowercase SHA-256 hex digest');
  }
  return value;
}

/** Strict structural reader for the trusted-row shadow metadata. */
export function validateRuntimeContractRefV1(value: unknown, path = 'runtimeContract'): RuntimeContractRefV1 {
  const v = record(value, path);
  ownOnly(v, path, ['path', 'sha256', 'schemaVersion', 'protocolVersion', 'capabilityDigest', 'features']);
  if (typeof v.path !== 'string' || !STORAGE_PATH.test(v.path)) {
    fail(`${path}.path`, 'must be a non-empty gs:// object path');
  }
  if (v.schemaVersion !== 1) fail(`${path}.schemaVersion`, 'must equal 1');
  if (v.protocolVersion !== 1 && v.protocolVersion !== 2) {
    fail(`${path}.protocolVersion`, 'must equal 1 or 2');
  }
  if (!Array.isArray(v.features)) fail(`${path}.features`, 'must be an array');
  const features: RuntimeContractFeature[] = [];
  for (let index = 0; index < v.features.length; index += 1) {
    const feature = v.features[index];
    if (typeof feature !== 'string' || !RUNTIME_CONTRACT_FEATURES.includes(feature as RuntimeContractFeature)) {
      fail(`${path}.features[${index}]`, `must be one of ${RUNTIME_CONTRACT_FEATURES.join(', ')}`);
    }
    if (features.includes(feature as RuntimeContractFeature)) {
      fail(`${path}.features[${index}]`, `duplicates ${JSON.stringify(feature)}`);
    }
    features.push(feature as RuntimeContractFeature);
  }
  const sorted = [...features].sort();
  if (features.some((feature, index) => feature !== sorted[index])) {
    fail(`${path}.features`, 'must be sorted by code-unit order');
  }
  return {
    path: v.path,
    sha256: digest(v.sha256, `${path}.sha256`),
    schemaVersion: 1,
    protocolVersion: v.protocolVersion,
    capabilityDigest: digest(v.capabilityDigest, `${path}.capabilityDigest`),
    features,
  };
}
